import { takeLatest, call, put, select, all } from 'redux-saga/effects';
import delay from '@redux-saga/delay-p';
import { CKForm } from 'console-kit';

import CollectionActions from './collection-actions';
import CollectionSelectors from './collection-selectors';
import CollectionApi from './collection-api';

const ANIMATION_DURATION = 760;

function* fetchCollection({ payload: { collectionId } }) {
  try {
    // check state before fetching from API
    let collection = yield select(CollectionSelectors.getCollectionById, collectionId);
    if (_.isEmpty(collection)) {
      collection = yield call(CollectionApi.fetchCollection, collectionId);
      yield put(CollectionActions.fetchCollectionSuccess(collection));
    }
    return collection;
  } catch (error) {
    yield put(CollectionActions.fetchCollectionFailure(error));
  }
}

function* fetchCollectionByExperience({ payload: { experienceId } }) {
  try {
    const collection = yield call(CollectionApi.fetchCollectionByExperience, experienceId);
    // invalid collections will not have an id
    if (!!collection.id) {
      yield put(CollectionActions.fetchCollectionByExperienceSuccess(collection));
    } else {
      yield put(CollectionActions.openWarningModalForMissingCampaignCollection());
    }
  } catch (error) {
    yield put(CollectionActions.fetchCollectionByExperienceFailure(error));
  }
}

function* fetchCollections() {
  try {
    let offset = 0;
    let allCollections = [];
    let allCount;
    do {
      const { count, collections } = yield call(CollectionApi.fetchCollections, undefined, offset, 500);
      allCount = count;
      allCollections = allCollections.concat(collections);
      offset = allCollections.length;
    } while (allCollections.length < allCount);
    yield put(CollectionActions.fetchCollectionsSuccess(allCollections));
  } catch (error) {
    yield put(CollectionActions.fetchCollectionsFailure(error));
  }
}

function* saveCollection({ payload }) {
  const { collection, collectionForm } = payload;
  try {
    yield put(CKForm.Actions.validateForm(collectionForm));
    const isValid = yield select(CKForm.Selectors.isFormValid, collectionForm);

    if (isValid) {
      yield put(CollectionActions.updateIsLoading(true));
      const collectionApiEndpoint = collection.isNew ? CollectionApi.createCollection : CollectionApi.updateCollection;
      const newCollection = yield call(collectionApiEndpoint, collection);
      yield put(CollectionActions.setActiveCollection(newCollection));
      yield put(CollectionActions.saveCollectionSuccess(newCollection));
    }
  } catch (error) {
    yield put(CollectionActions.saveCollectionFailure(error));
  }
}

// save and apply differ for navigation and animating button purposes
function* applyCollection({ payload }) {
  const { collection, collectionForm } = payload;
  try {
    yield put(CKForm.Actions.validateForm(collectionForm));
    const isValid = yield select(CKForm.Selectors.isFormValid, collectionForm);

    if (isValid) {
      yield put(CollectionActions.updateIsLoading(true));
      const collectionApiEndpoint = CollectionApi.updateCollection;
      const newCollection = yield call(collectionApiEndpoint, collection);
      yield put(CollectionActions.setActiveCollection(newCollection));
      yield put(CollectionActions.applyCollectionSuccess(newCollection));
    }
  } catch (error) {
    yield put(CollectionActions.applyCollectionFailure(error));
  }
}

function* deleteCollection({ payload: { collectionId } }) {
  if (collectionId === 'new') {
    // delete from state; no ajax request required
    yield put(CollectionActions.deleteCollectionSuccess(collectionId));
    return;
  }
  try {
    yield call(CollectionApi.deleteCollection, collectionId);
    yield put(CollectionActions.deleteCollectionSuccess(collectionId));
    yield call(delay, ANIMATION_DURATION); // allow animating button to finish
    yield put(CollectionActions.closeDeleteCollectionModal());
  } catch (error) {
    yield put(CollectionActions.deleteCollectionFailure(error));
  }
}

function* clearCollectionContent({ payload: { collectionId, keepSeeded } }) {
  try {
    yield call(CollectionApi.clearCollectionContent, collectionId, keepSeeded);
    yield put(CollectionActions.clearCollectionContentSuccess());
    yield call(delay, ANIMATION_DURATION); // allow animating button to finish
    yield put(CollectionActions.closeClearCollectionModal());
  } catch (error) {
    yield put(CollectionActions.clearCollectionContentFailure(error));
  }
}

export default function* collectionSaga() {
  yield all([
    takeLatest(CollectionActions.fetchCollection.getType(), fetchCollection),
    takeLatest(CollectionActions.fetchCollectionByExperience.getType(), fetchCollectionByExperience),
    takeLatest(CollectionActions.fetchCollections.getType(), fetchCollections),
    takeLatest(CollectionActions.saveCollection.getType(), saveCollection),
    takeLatest(CollectionActions.applyCollection.getType(), applyCollection),
    takeLatest(CollectionActions.deleteCollection.getType(), deleteCollection),
    takeLatest(CollectionActions.clearCollectionContent.getType(), clearCollectionContent),
  ]);
}

export { fetchCollection };
